import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { GuardrailComposition } from "../../../../../http/border/types/guardrailCompositions";
import { AdminBorderSystemNormative } from "../../../../../http/border/types/systemNormatives";
import {
  AdminBorderUprightMapping,
  UpsertBorderUprightMappingBody,
} from "../../../../../http/border/types/uprightMappings";
import { Upright } from "../../../../../http/border/types/uprights";
import { useGuardrailCompositionsApi } from "../../../../../http/border/useGuardrailCompositions";
import { useSystemNormativesApi } from "../../../../../http/border/useSystemNormatives";
import { useUprightMappingsApi } from "../../../../../http/border/useUprightMappings";
import { useUprightsApi } from "../../../../../http/border/useUprights";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";

interface UprightMappingFormBody {
  systemNormative: AdminBorderSystemNormative | null;
  guardrailComposition: GuardrailComposition | null;
  upright: Upright | null;
  isGateVisible: boolean;
  minCurbHeight: number | null;
}

interface UprightMappingFormProps {
  uprightMapping?: AdminBorderUprightMapping;
  onClose: () => void;
  isDisabled?: boolean;
}

const toUprightOption = (uprightMapping?: AdminBorderUprightMapping): Upright | null =>
  uprightMapping?.upright
    ? {
        ...uprightMapping.upright,
        lowerHoleDiameter: uprightMapping.upright.lowerHoleDiameter ?? undefined,
        uprightCap: undefined,
      }
    : null;

const toGuardrailCompositionOption = (uprightMapping?: AdminBorderUprightMapping): GuardrailComposition | null =>
  uprightMapping?.guardrailComposition
    ? {
        ...uprightMapping.guardrailComposition,
        nameIt: null,
        nameDe: null,
        nameEs: null,
        nameFr: null,
      }
    : null;

const UprightMappingForm: FC<UprightMappingFormProps> = ({
  uprightMapping,
  onClose,
  isDisabled,
}: UprightMappingFormProps) => {
  const i18n = useI18n();
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultSystemNormativeOptions, setDefaultSystemNormativeOptions] = useState<AdminBorderSystemNormative[]>([]);
  const [defaultGuardrailCompositionOptions, setDefaultGuardrailCompositionOptions] = useState<GuardrailComposition[]>(
    [],
  );
  const [defaultUprightOptions, setDefaultUprightOptions] = useState<Upright[]>([]);

  const systemNormativesApi = useSystemNormativesApi();
  const guardrailCompositionsApi = useGuardrailCompositionsApi();
  const uprightsApi = useUprightsApi();
  const uprightMappingsApi = useUprightMappingsApi();

  const defaultValues: UprightMappingFormBody = {
    systemNormative: uprightMapping?.systemNormative ?? null,
    guardrailComposition: toGuardrailCompositionOption(uprightMapping),
    upright: toUprightOption(uprightMapping),
    isGateVisible: uprightMapping?.isGateVisible ?? false,
    minCurbHeight: uprightMapping?.minCurbHeight ?? null,
  };

  const onSubmit = () => async (formData: UprightMappingFormBody) => {
    setIsSubmitting(true);

    const body: UpsertBorderUprightMappingBody = {
      systemNormativeId: formData.systemNormative?.id!,
      guardrailCompositionId: formData.guardrailComposition?.id!,
      uprightId: formData.upright?.item.id!,
      isGateVisible: formData.isGateVisible,
      minCurbHeight: formData.minCurbHeight,
    };

    try {
      if (uprightMapping?.id) {
        await uprightMappingsApi.updateAdminUprightMapping(borderSystemId!, uprightMapping.id, body);
      } else {
        await uprightMappingsApi.insertAdminUprightMapping(borderSystemId!, body);
      }
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<UprightMappingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue, watch, reset }) => {
        const isGateVisible = !!watch("isGateVisible");

        useEffect(() => {
          reset(defaultValues);
        }, [uprightMapping]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="systemNormative"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.normatives.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, (query, signal) =>
                      systemNormativesApi.getAdminSystemNormatives(borderSystemId!, query, signal),
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultSystemNormativeOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultSystemNormativeOptions}
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => x.normative.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("systemNormative", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="guardrailComposition"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.guardrailComposition} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(
                      aborter,
                      searchTerm,
                      guardrailCompositionsApi.getAdminGuardrailCompositions,
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultGuardrailCompositionOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultGuardrailCompositionOptions}
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("guardrailComposition", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="upright"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.uprights.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());
                    const loadOptions = createLoadOptions(aborter, searchTerm, uprightsApi.getAdminUprights);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUprightOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultUprightOptions}
                        isDisabled={isDisabled || isSubmitting}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("upright", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <FormField label={i18n.translation.common.isGateVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isSubmitting}
                  id={labelId}
                  checked={isGateVisible}
                  onChange={(checked) => setValue("isGateVisible", checked)}
                />
              )}
            </FormField>

            <Controller
              name="minCurbHeight"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minCurbHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => setValue("minCurbHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UprightMappingForm;
